import { useMemo } from 'react';
import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function DataValidationIndicator() {
    const { mergedMeasurementDataList, ts } = useOptimizationPipeline();

    const { rows, reasons } = useMemo(() => {
        if (!Array.isArray(mergedMeasurementDataList) || !ts) return { rows: [], reasons: [] };

        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return { rows: [], reasons: [] };

        const entry = mergedMeasurementDataList.find(e => {
            const t = e?.ts ? new Date(e.ts).getTime() : NaN;
            return !Number.isNaN(t) && t === target;
        });
        if (!entry) return { rows: [], reasons: [] };

        const od = entry?.meta?.outlier_detection;

        const details = od?.details;
        const toRow = (d, idx) => {
            const name = d?.outlier_detector_name ? String(d.outlier_detector_name) : '';
            const ver = (d?.outlier_detector_version ?? '') !== '' ? ` v${d.outlier_detector_version}` : '';
            const detectorLabel = (name || ver) ? `${name}${ver}` : '-';
            const prediction = (typeof d?.prediction === 'boolean') ? d.prediction : undefined;
            return { id: idx, detectorLabel, prediction };
        };

        let rows = [];
        if (Array.isArray(details)) rows = details.map((d, i) => toRow(d, i));
        else if (details && typeof details === 'object') rows = [toRow(details, 0)];

        const skipped = od?.skipped_because_of;
        const reasons = Array.isArray(skipped) ? skipped.map(v => (v == null ? '' : String(v))).filter(Boolean) : [];

        return { rows, reasons };
    }, [mergedMeasurementDataList, ts]);

    const predictionBadge = (pred) => {
        const bg = pred === true ? 'bg-red-100' : pred === false ? 'bg-green-100' : 'bg-gray-100';
        const dim = (pred === true || pred === false) ? '' : 'opacity-50';
        return (
            <div className={'w-full lg:w-auto text-center font-semibold px-4 py-2 rounded-full lg:rounded ' + bg + ' ' + dim + (pred === true ? ' text-red-900' : pred === false ? ' text-green-900' : '')}>
                {pred === true ? 'Yes' : pred === false ? 'No' : '-'}
            </div>
        );
    };

    return (
        <div className='w-full flex flex-col gap-2 shadow-inner p-4 bg-white rounded-xl border text-11'>

            {rows.length > 0 && rows.map(row => (
                <div key={row.id} className='flex flex-col lg:flex-row items-center justify-between gap-4 border-b last:border-0 px-4 py-2'>
                    <span className='text-10 whitespace-nowrap'>{row.detectorLabel}</span>
                    {predictionBadge(row.prediction)}
                </div>
            ))}

            {rows.length === 0 && reasons.length > 0 && (
                <div className='flex flex-col gap-4 px-4 py-2'>
                    <div className='text-11 font-semibold'>Skipped because of</div>
                    <div className='flex flex-wrap gap-2'>
                        {reasons.map((r, i) => (<span key={r + '_' + i} className='px-3 py-1 rounded bg-amber-100 text-amber-900 text-10'>{r}</span>))}
                    </div>
                </div>
            )}

            {rows.length === 0 && reasons.length === 0 && (<div className='px-8 py-1 rounded-lg bg-gray-200 opacity-50'>No details</div>)}

        </div>
    );
}
